import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../../firebase';
import { EditIcon, DeleteIcon, GoalIcon } from '../../icons';
import { IkigaiData, IkigaiAnswers, IKIGAI_STEPS } from './types';
import { getIkigai, saveIkigai, deleteIkigai } from './ikigai.service';
import IkigaiForm from './IkigaiForm';
import IkigaiFlower from './IkigaiFlower';

type ViewState = 'loading' | 'empty' | 'form' | 'result';

/**
 * IkigaiPage - Main Purpose / Ikigai tab content.
 * Shows empty state → form (4 steps) → result with flower diagram.
 * Editable and deletable.
 */
const IkigaiPage: React.FC = () => {
  const [user] = useAuthState(auth);
  const [view, setView] = useState<ViewState>('loading');
  const [ikigaiData, setIkigaiData] = useState<IkigaiData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    loadIkigai();
  }, [user]);

  const loadIkigai = async () => {
    if (!user) return;
    setView('loading');
    setError(null);
    try {
      const data = await getIkigai(user.uid);
      if (data) {
        setIkigaiData(data);
        setView('result');
      } else {
        setView('empty');
      }
    } catch {
      setError('Kon Purpose gegevens niet laden.');
      setView('empty');
    }
  };

  const handleFormComplete = async (answers: IkigaiAnswers) => {
    if (!user) return;
    setIsSaving(true);
    setError(null);
    try {
      await saveIkigai(user.uid, answers, ikigaiData?.purposeSummary);
      const updated = await getIkigai(user.uid);
      setIkigaiData(updated);
      setView('result');
    } catch {
      setError('Opslaan mislukt. Probeer het opnieuw.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteIkigai(user.uid);
      setIkigaiData(null);
      setShowDeleteConfirm(false);
      setView('empty');
    } catch {
      setError('Verwijderen mislukt. Probeer het opnieuw.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Loading
  if (view === 'loading') {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-sm text-gray-400">Laden...</div>
      </div>
    );
  }

  // Form
  if (view === 'form') {
    return (
      <div className="py-4">
        {error && (
          <div className="max-w-xl mx-auto mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}
        <IkigaiForm
          initialAnswers={ikigaiData?.answers}
          onComplete={handleFormComplete}
          onCancel={() => setView(ikigaiData ? 'result' : 'empty')}
          isSaving={isSaving}
        />
      </div>
    );
  }

  // Empty state
  if (view === 'empty') {
    return (
      <div className="py-8 px-4">
        <div className="max-w-xl mx-auto text-center">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: 'rgba(141,168,160,0.1)' }}
          >
            <GoalIcon size={26} style={{ color: '#7A9488' }} />
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Jouw Purpose is nog niet ingevuld
          </h3>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed max-w-sm mx-auto">
            Ontdek het kruispunt van wat jij liefhebt, waar je goed in bent,
            wat de wereld nodig heeft en waarvoor je betaald kunt worden.
          </p>

          <button
            onClick={() => setView('form')}
            className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#7A9488' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#6B8579'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#7A9488'; }}
          >
            Purpose ontdekken
          </button>

          {/* Preview of what it covers */}
          <div className="mt-8 grid grid-cols-2 gap-3 text-left">
            {[
              { label: 'Waar hou je van?', color: 'border-rose-200 bg-rose-50', text: 'text-rose-700' },
              { label: 'Waar ben je goed in?', color: 'border-amber-200 bg-amber-50', text: 'text-amber-700' },
              { label: 'Wat heeft de wereld nodig?', color: 'border-teal-200 bg-teal-50', text: 'text-teal-700' },
              { label: 'Waarvoor kun je betaald worden?', color: 'border-violet-200 bg-violet-50', text: 'text-violet-700' },
            ].map(item => (
              <div key={item.label} className={`rounded-lg border p-3 text-xs font-medium ${item.color} ${item.text}`}>
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Result view
  if (view === 'result' && ikigaiData) {
    return (
      <div className="py-4 px-4">
        {error && (
          <div className="max-w-xl mx-auto mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Header */}
        <div className="max-w-xl mx-auto mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Jouw Purpose</h2>
              {ikigaiData.updatedAt && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Bijgewerkt op {new Date(ikigaiData.updatedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setView('form')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors"
                style={{ borderColor: '#D1D5DB', color: '#6B7280' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <EditIcon size={13} />
                Bewerken
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors"
                style={{ borderColor: '#FECACA', color: '#EF4444' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#FEF2F2'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <DeleteIcon size={13} />
                Verwijderen
              </button>
            </div>
          </div>
        </div>

        {/* Flower diagram */}
        <IkigaiFlower
          answers={ikigaiData.answers}
          purposeSummary={ikigaiData.purposeSummary}
        />

        {/* Detailed answers */}
        <div className="max-w-xl mx-auto mt-8">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Jouw antwoorden</h3>
          <div className="space-y-3">
            {IKIGAI_STEPS.map(config => (
              <details key={config.step} className={`rounded-lg border ${config.borderColor} overflow-hidden`}>
                <summary
                  className={`cursor-pointer select-none px-4 py-3 text-xs font-semibold ${config.textColor} ${config.color} flex items-center justify-between`}
                >
                  {config.titleNL}
                  <span className="text-gray-400 font-normal text-xs">Klik om te bekijken</span>
                </summary>
                <div className="px-4 py-3 bg-white space-y-3">
                  {config.questions.map(q => (
                    <div key={q.field}>
                      <p className="text-xs font-medium text-gray-500 mb-0.5">{q.labelNL}</p>
                      <p className="text-sm text-gray-800 leading-relaxed">
                        {ikigaiData.answers[q.field] || <span className="italic text-gray-400">Niet ingevuld</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Delete confirm modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
            <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Purpose verwijderen?</h3>
              <p className="text-sm text-gray-500 mb-5">
                Al jouw antwoorden worden permanent verwijderd. Dit kan niet ongedaan worden gemaakt.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isDeleting ? 'Verwijderen...' : 'Ja, verwijderen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default IkigaiPage;
